/**
 * Authentication (docs/12 identity provider): WorkOS AuthKit hosted sign-in in deployed
 * environments; locally and in CI the API's dev-token adapter (`POST /v1/auth/dev-token`), which
 * the sign-in page offers only when `/v1/auth/config` says `dev-token` and the build is not a
 * production build. The token is kept in localStorage and sent as a bearer by @oex/contracts.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { orgStore, tokenStore } from './api';

type AuthApi = {
  token: string | null;
  signIn(token: string, refreshToken?: string | null): void;
  signOut(): void;
  /** Staff impersonation: park the staff token, act with the impersonation token. */
  startImpersonation(token: string): void;
  endImpersonation(): string | null;
  impersonating: boolean;
};

const AuthContext = createContext<AuthApi | null>(null);

export function AuthProvider({ children, onChange }: { children: ReactNode; onChange?(): void }) {
  const [token, setToken] = useState<string | null>(() => tokenStore.get());
  const [impersonating, setImpersonating] = useState<boolean>(() => Boolean(tokenStore.getStaff()));
  const signIn = useCallback(
    (next: string, refreshToken: string | null = null) => {
      tokenStore.set(next);
      tokenStore.setRefresh(refreshToken);
      setToken(next);
      onChange?.();
    },
    [onChange],
  );
  const signOut = useCallback(() => {
    tokenStore.set(null);
    tokenStore.setRefresh(null);
    tokenStore.setStaff(null);
    tokenStore.setStaffRefresh(null);
    orgStore.set(null);
    setToken(null);
    setImpersonating(false);
    onChange?.();
  }, [onChange]);
  const startImpersonation = useCallback(
    (impersonationToken: string) => {
      // Park the staff token and its refresh token; the impersonation token has no refresh.
      tokenStore.setStaff(tokenStore.get());
      tokenStore.setStaffRefresh(tokenStore.getRefresh());
      tokenStore.set(impersonationToken);
      tokenStore.setRefresh(null);
      orgStore.set(null);
      setToken(impersonationToken);
      setImpersonating(true);
      onChange?.();
    },
    [onChange],
  );
  const endImpersonation = useCallback(() => {
    const staff = tokenStore.getStaff();
    tokenStore.set(staff);
    tokenStore.setRefresh(tokenStore.getStaffRefresh());
    tokenStore.setStaff(null);
    tokenStore.setStaffRefresh(null);
    orgStore.set(null);
    setToken(staff);
    setImpersonating(false);
    onChange?.();
    return staff;
  }, [onChange]);
  const value = useMemo(
    () => ({ token, signIn, signOut, startImpersonation, endImpersonation, impersonating }),
    [token, signIn, signOut, startImpersonation, endImpersonation, impersonating],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthApi {
  const api = useContext(AuthContext);
  if (!api) throw new Error('useAuth outside <AuthProvider>');
  return api;
}

/** The dev-token form is never offered by a production build (docs/12). */
export const DEV_LOGIN_ALLOWED =
  import.meta.env.MODE !== 'production' || import.meta.env.VITE_ALLOW_DEV_LOGIN === 'true';
